import type { ComboboxOption } from '@repo/design-system';
import type { CompositeRefLabel } from '@repo/ui';
import type { FloorPlannerCompositeRef, FloorPlannerLayout } from '@repo/frontend-types';
import {
  asArray,
  decodeRef,
  encodeRef,
  entityNamesByRefid,
  isLive,
  layoutCurrentDesigns,
} from '@repo/front-end-utils';

/**
 * What a complex measure can point at, as the picker sees it — and the one place a
 * `FloorPlannerCompositeRef` becomes a string and back.
 *
 * ## The label is not the identity, and that is the whole point
 *
 * A plan's display names are not unique: two zones can both be called "Rack 6", and until this
 * feature the wizard defended against that with `findBuildCollisions`, which refused to build
 * anything at all when two entities shared a base name. On the plan a member is a `refid`, which
 * IS unique — `findDuplicateEntityCodes` already blocks saving a plan whose entity codes collide —
 * so two same-named entities are simply two options, told apart by the entity code in `meta`.
 * The guard has nothing left to defend.
 *
 * ## One encoder, shared by the row and the picker
 *
 * The panel renders a reference and the editor picks one. If each carried its own idea of what a
 * reference's label is, a measure could read one way in the list and another in the form — the
 * same class of defect as two copies of a resolution rule, one level down. So `labelForRef` reads
 * the same option list the `Combobox` is given.
 *
 * `encodeRef` / `decodeRef` now live in `@repo/front-end-utils` (`twin-planner/compositeRefs.ts`)
 * and are re-exported here unchanged.
 */

export { encodeRef, decodeRef };

/**
 * Everything on this plan a measure can total or add up, in reading order: the twin, then its
 * floors, then its entities.
 *
 * A floor with no numeric `id` is **left out**, which matches what the load already does with one:
 * `matchCompositesToPlan` tracks those separately and reports them rather than guessing which
 * storey a total belongs to. Offering one here would let an operator pick a floor the plan cannot
 * identify, and the pick would be unrepresentable.
 */
export const compositeRefOptions = (
  layout: FloorPlannerLayout | null | undefined,
  twinBid: string | undefined,
  twinName?: string,
): ComboboxOption[] => {
  const options: ComboboxOption[] = [
    {
      value: encodeRef({ kind: 'twin' }),
      label: twinName?.trim() || twinBid || 'Whole twin',
      meta: 'Whole twin',
    },
  ];

  for (const floor of asArray(layout?.floors)) {
    if (typeof floor?.id !== 'number') continue;
    options.push({
      value: encodeRef({ kind: 'floor', floorId: floor.id }),
      label: floor.name?.trim() || `Floor ${floor.id}`,
      meta: 'Floor',
    });
  }

  // The CURRENT design of each floor, which is what every other reader in this feature uses and
  // what `setAllFloorObservers` writes back into.
  const names = entityNamesByRefid(layout ?? null);
  for (const design of layoutCurrentDesigns(layout ?? null)) {
    for (const surface of asArray(design?.surfaces).filter(isLive)) {
      if (typeof surface.refid !== 'string' || !surface.refid) continue;
      options.push({
        value: encodeRef({ kind: 'entity', refid: surface.refid, entityKind: 'surface' }),
        label: names.get(surface.refid) ?? surface.refid,
        meta: surface.entityCode || 'Zone',
      });
    }
    for (const item of asArray(design?.items).filter(isLive)) {
      if (typeof item.refid !== 'string' || !item.refid) continue;
      options.push({
        value: encodeRef({ kind: 'entity', refid: item.refid, entityKind: 'item' }),
        label: names.get(item.refid) ?? item.refid,
        meta: item.entityCode || 'Equipment',
      });
    }
  }

  return options;
};

/*
 * The SAME type the panel renders, imported rather than restated.
 *
 * A second declaration of `{label, meta?, unresolved}` here would compile against the panel by
 * structural typing and then drift the moment either side gained a field — and the drift would be
 * silent, because structural typing keeps accepting the older shape until something is actually
 * missing. `import type` is erased, so this pulls nothing from the `@repo/ui` barrel at runtime.
 */
export type { CompositeRefLabel as RefLabel };

/**
 * How a reference reads on screen.
 *
 * A reference the options do not hold is **not** an error to swallow: an entity deleted on the
 * Floor Plan stage after a measure was drawn produces one, and dropping the row would delete a
 * measure the operator did not delete.
 */
export const labelForRef = (
  ref: FloorPlannerCompositeRef | null | undefined,
  options: ReadonlyArray<ComboboxOption>,
): CompositeRefLabel => {
  const value = encodeRef(ref);
  const hit = value === '' ? undefined : options.find(o => o.value === value);
  if (hit) return { label: hit.label, meta: hit.meta, unresolved: false };

  // A malformed reference reads as unreadable and stays deletable — the same treatment R17 gives a
  // reference the plan no longer has, for the same reason: never hide a measure nobody deleted.
  if (value === '')
    return { label: 'unreadable', meta: 'not a thing on this plan', unresolved: true };

  const raw =
    ref!.kind === 'twin'
      ? 'the whole twin'
      : ref!.kind === 'floor'
        ? `floor ${ref!.floorId}`
        : ref!.refid;
  return { label: raw, meta: 'no longer on the plan', unresolved: true };
};
